import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import { Container } from '@/components/container';
import PriceInput from '@/components/inputControls/PriceInput';
import { useSnackbar } from 'notistack';
import { KeenIcon } from '@/components';
import CountryCodeSelect from '@/components/CountryCodeSelect/CountryCodeSelect';
import { ProductType, RegimeLogic } from '@/constant';
import {
  useCreateProductVatConfigMutation,
  useGetProductVatConfigByIdQuery,
  useUpdateProductVatConfigMutation,
} from '@/services/ProductVatConfigService';
import {
  ProductVatConfigCreatePayload,
  ProductVatConfigData,
} from '@/types/product-vat-config.types';
import {
  defaultProductVatConfigScreen,
  type ProductVatConfigScreenConfig,
} from './productVatConfigScreenConfig';

interface ProductVatConfigFormProps {
  mode: 'create' | 'edit';
  screenConfig?: ProductVatConfigScreenConfig;
}

type FormState = Omit<ProductVatConfigCreatePayload, 'default_vat_rate'> & {
  default_vat_rate: number | '';
};

const productTypeLabels: Record<ProductType, string> = {
  [ProductType.HARBOUR_BERTH]: 'Harbour berth',
  [ProductType.HARBOUR_SERVICE]: 'Harbour service',
  [ProductType.HARBOUR_ADDON]: 'Harbour addon',
  [ProductType.SAARISTO_SUBSCRIPTION]: 'Saaristo subscription',
  [ProductType.CLUB_RESOURCE]: 'Club resource',
  [ProductType.SERVICE_BOOKING]: 'Service booking',
  [ProductType.OTHER]: 'Other',
};

const regimeLogicLabels: Record<RegimeLogic, string> = {
  [RegimeLogic.DOMESTIC_ONLY]: 'Domestic only',
  [RegimeLogic.BY_CUSTOMER_COUNTRY]: 'By customer country',
  [RegimeLogic.IOSS_ELIGIBLE]: 'IOSS eligible',
};

const ProductVatConfigForm = ({
  mode,
  screenConfig = defaultProductVatConfigScreen,
}: ProductVatConfigFormProps) => {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();

  const isEdit = mode === 'edit';
  const configId = isEdit ? params.id : undefined;
  const isViewMode = searchParams.get('mode') === 'view';
  const isReadOnly = isViewMode;

  const restrictedProductTypes = screenConfig.restrictedOptions?.productTypes;
  const restrictedRegimeLogics = screenConfig.restrictedOptions?.regimeLogics;
  const restrictedDomesticCountryCode =
    screenConfig.restrictedOptions?.domesticCountryCode;
  const hideProductTypeField = screenConfig.hideProductTypeField === true;

  const availableProductTypes = useMemo(
    () =>
      hideProductTypeField
        ? []
        : restrictedProductTypes && restrictedProductTypes.length > 0
          ? restrictedProductTypes
          : (Object.values(ProductType) as ProductType[]),
    [hideProductTypeField, restrictedProductTypes]
  );

  const getAllowedRegimesForProductType = useCallback(
    (productType?: ProductType) => {
      if (restrictedRegimeLogics && restrictedRegimeLogics.length > 0) {
        return restrictedRegimeLogics;
      }

      return productType === ProductType.SAARISTO_SUBSCRIPTION
        ? [RegimeLogic.BY_CUSTOMER_COUNTRY, RegimeLogic.IOSS_ELIGIBLE]
        : [RegimeLogic.DOMESTIC_ONLY, RegimeLogic.IOSS_ELIGIBLE];
    },
    [restrictedRegimeLogics]
  );

  const [createConfig, { isLoading: isCreating }] =
    useCreateProductVatConfigMutation();
  const [updateConfig, { isLoading: isUpdating }] =
    useUpdateProductVatConfigMutation();

  const initialProductType =
    screenConfig.forcePayload?.product_type ?? availableProductTypes[0];

  const [formData, setFormData] = useState<FormState>({
    product_type: initialProductType,
    config_type: screenConfig.forcePayload?.config_type,
    default_vat_rate: '',
    default_regime_logic:
      getAllowedRegimesForProductType(initialProductType)[0],
    eligible_for_ioss: screenConfig.forcePayload?.eligible_for_ioss ?? false,
    active_from: '',
    active_to: '',
    domestic_country_code: restrictedDomesticCountryCode || '',
    customer_country_code: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: configDetail } = useGetProductVatConfigByIdQuery(configId!, {
    skip: !isEdit || !configId,
  });

  useEffect(() => {
    if (!configDetail || !isEdit) {
      return;
    }

    const config: ProductVatConfigData = configDetail;
    const toDateOnly = (value?: string) =>
      value && value.includes('T') ? value.split('T')[0] : value || '';

    const incomingProductType = config.product_type as ProductType;
    const normalizedProductType = hideProductTypeField
      ? undefined
      : restrictedProductTypes && restrictedProductTypes.length > 0
        ? restrictedProductTypes[0]
        : incomingProductType;
    const incomingRegime = config.default_regime_logic as RegimeLogic;
    const allowedRegimes = getAllowedRegimesForProductType(
      normalizedProductType
    );
    const normalizedRegime = allowedRegimes.includes(incomingRegime)
      ? incomingRegime
      : allowedRegimes[0];

    setFormData({
      product_type: normalizedProductType,
      config_type: screenConfig.forcePayload?.config_type,
      default_vat_rate: config.default_vat_rate,
      default_regime_logic: normalizedRegime,
      eligible_for_ioss:
        screenConfig.forcePayload?.eligible_for_ioss ??
        normalizedRegime === RegimeLogic.IOSS_ELIGIBLE,
      active_from: toDateOnly(config.active_from),
      active_to: toDateOnly(config.active_to),
      domestic_country_code:
        restrictedDomesticCountryCode || config.domestic_country_code || '',
      customer_country_code: config.customer_country_code || '',
    });
    setErrors({});
  }, [
    configDetail,
    getAllowedRegimesForProductType,
    hideProductTypeField,
    isEdit,
    restrictedDomesticCountryCode,
    restrictedProductTypes,
    screenConfig.forcePayload?.eligible_for_ioss,
    screenConfig.forcePayload?.config_type,
  ]);

  const schema = useMemo(
    () =>
      Yup.object().shape({
        product_type: hideProductTypeField
          ? Yup.mixed<ProductType>().notRequired()
          : Yup.mixed<ProductType>()
              .oneOf(Object.values(ProductType) as ProductType[])
              .required('Product type is required'),
        default_vat_rate: Yup.number()
          .transform((value, originalValue) =>
            originalValue === '' ? undefined : value
          )
          .typeError('VAT rate must be a number')
          .min(0, 'VAT must be >= 0')
          .max(100, 'VAT must be <= 100')
          .required('VAT rate is required'),
        default_regime_logic: Yup.mixed<RegimeLogic>().oneOf(
          Object.values(RegimeLogic) as RegimeLogic[]
        ),
        eligible_for_ioss: Yup.boolean().required(),
        active_from: Yup.date()
          .transform((value, originalValue) =>
            originalValue === '' ? undefined : value
          )
          .typeError('Select valid start date')
          .notRequired(),
        active_to: Yup.date()
          .transform((value, originalValue) =>
            originalValue === '' ? undefined : value
          )
          .typeError('Select valid end date')
          .when('active_from', (from: unknown, schema: Yup.DateSchema) =>
            from
              ? schema.min(
                  Yup.ref('active_from'),
                  'End date must be after start date'
                )
              : schema
          )
          .notRequired(),
        domestic_country_code: Yup.string().when(
          ['product_type', 'default_regime_logic'],
          (values, schema) => {
            const [productType, regime] = values as unknown as [
              ProductType,
              RegimeLogic,
            ];

            if (
              productType !== ProductType.SAARISTO_SUBSCRIPTION &&
              regime === RegimeLogic.DOMESTIC_ONLY
            ) {
              return schema.required('Country is required');
            }

            return schema.notRequired();
          }
        ),
        customer_country_code: Yup.string().when(
          ['product_type', 'default_regime_logic'],
          (values, schema) => {
            const [productType, regime] = values as unknown as [
              ProductType,
              RegimeLogic,
            ];

            if (
              productType === ProductType.SAARISTO_SUBSCRIPTION &&
              regime === RegimeLogic.BY_CUSTOMER_COUNTRY
            ) {
              return schema.required('Country is required');
            }

            return schema.notRequired();
          }
        ),
      }),
    [hideProductTypeField]
  );

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const name = target.name as keyof ProductVatConfigCreatePayload;
    let nextValue: string | number | boolean = target.value;

    if (target instanceof HTMLInputElement && target.type === 'checkbox') {
      nextValue = target.checked;
    } else if (target instanceof HTMLInputElement && target.type === 'number') {
      const raw = target.value;
      if (raw === '') {
        nextValue = '';
      } else {
        const parsedValue = Number(raw);
        nextValue = Number.isNaN(parsedValue) ? '' : parsedValue;
      }
    }

    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: nextValue as never,
      };

      if (name === 'product_type') {
        const nextProductType = nextValue as ProductType;
        const allowedRegimes = getAllowedRegimesForProductType(nextProductType);
        const nextRegime = allowedRegimes.includes(
          updated.default_regime_logic as RegimeLogic
        )
          ? (updated.default_regime_logic as RegimeLogic)
          : allowedRegimes[0];

        return {
          ...updated,
          default_regime_logic: nextRegime,
          eligible_for_ioss:
            screenConfig.forcePayload?.eligible_for_ioss ??
            nextRegime === RegimeLogic.IOSS_ELIGIBLE,
        };
      }

      if (name === 'default_regime_logic') {
        return {
          ...updated,
          eligible_for_ioss:
            screenConfig.forcePayload?.eligible_for_ioss ??
            nextValue === RegimeLogic.IOSS_ELIGIBLE,
        };
      }

      return updated;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await schema.validate(formData, { abortEarly: false });
      setErrors({});

      const payload: ProductVatConfigCreatePayload = {
        ...(formData.product_type
          ? { product_type: formData.product_type }
          : {}),
        ...(formData.config_type ? { config_type: formData.config_type } : {}),
        default_vat_rate:
          typeof formData.default_vat_rate === 'string'
            ? Number(formData.default_vat_rate)
            : formData.default_vat_rate,
        default_regime_logic: formData.default_regime_logic,
        eligible_for_ioss: formData.eligible_for_ioss,
        ...(formData.active_from ? { active_from: formData.active_from } : {}),
        ...(formData.active_to ? { active_to: formData.active_to } : {}),
        ...(formData.product_type !== ProductType.SAARISTO_SUBSCRIPTION &&
        formData.default_regime_logic === RegimeLogic.DOMESTIC_ONLY &&
        formData.domestic_country_code
          ? { domestic_country_code: formData.domestic_country_code }
          : {}),
        ...(formData.product_type === ProductType.SAARISTO_SUBSCRIPTION &&
        formData.default_regime_logic === RegimeLogic.BY_CUSTOMER_COUNTRY &&
        formData.customer_country_code
          ? { customer_country_code: formData.customer_country_code }
          : {}),
        ...(screenConfig.forcePayload ?? {}),
      };

      if (isEdit && configId) {
        await updateConfig({ id: configId, payload }).unwrap();
        enqueueSnackbar(`${screenConfig.entityLabel} updated successfully`, {
          variant: 'success',
        });
      } else {
        await createConfig(payload).unwrap();
        enqueueSnackbar(`${screenConfig.entityLabel} created successfully`, {
          variant: 'success',
        });
      }

      navigate(screenConfig.basePath);
    } catch (err: unknown) {
      if (err instanceof Yup.ValidationError) {
        const newErrors: Record<string, string> = {};
        err.inner.forEach((validationError) => {
          if (validationError.path) {
            newErrors[validationError.path] = validationError.message;
          }
        });
        setErrors(newErrors);
        return;
      }

      const apiMessage = (err as { data?: { message?: string } })?.data
        ?.message;
      enqueueSnackbar(
        apiMessage || `Failed to submit ${screenConfig.entityLabel}`,
        {
          variant: 'error',
        }
      );
    }
  };

  const availableRegimes = getAllowedRegimesForProductType(
    formData.product_type
  );

  return (
    <Container>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <KeenIcon icon="euro" />
            <h1 className="text-base font-semibold">
              {isViewMode
                ? `View ${screenConfig.formTitle}`
                : isEdit
                  ? `Edit ${screenConfig.formTitle}`
                  : `Create ${screenConfig.formTitle}`}
            </h1>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {!hideProductTypeField && (
              <div>
                <label className="form-label" htmlFor="product_type">
                  Product Type
                </label>
                <select
                  id="product_type"
                  name="product_type"
                  value={formData.product_type}
                  onChange={handleChange}
                  className="select w-full"
                  disabled={isReadOnly || availableProductTypes.length === 1}
                >
                  {availableProductTypes.map((productType) => (
                    <option key={productType} value={productType}>
                      {productTypeLabels[productType]}
                    </option>
                  ))}
                </select>
                {errors.product_type && (
                  <div className="text-danger contrast:text-red-500 text-xs mt-1">
                    {errors.product_type}
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="form-label" htmlFor="default_vat_rate">
                Default VAT Rate (%)
              </label>
              <PriceInput
                id="default_vat_rate"
                name="default_vat_rate"
                value={formData.default_vat_rate}
                onChange={(value) => {
                  setFormData((prev) => ({
                    ...prev,
                    default_vat_rate: value ?? '',
                  }));
                }}
                inputClassName="input w-full"
                min={0}
                max={100}
                step={0.01}
                autoComplete="off"
                disabled={isReadOnly}
              />
              {errors.default_vat_rate && (
                <div className="text-danger contrast:text-red-500 text-xs mt-1">
                  {errors.default_vat_rate}
                </div>
              )}
            </div>

            <div>
              <label className="form-label" htmlFor="default_regime_logic">
                Default Regime Logic
              </label>
              <select
                id="default_regime_logic"
                name="default_regime_logic"
                value={formData.default_regime_logic}
                onChange={handleChange}
                className="select w-full"
                disabled={isReadOnly || availableRegimes.length === 1}
              >
                {availableRegimes.map((regimeLogic) => (
                  <option key={regimeLogic} value={regimeLogic}>
                    {regimeLogicLabels[regimeLogic]}
                  </option>
                ))}
              </select>
              {errors.default_regime_logic && (
                <div className="text-danger contrast:text-red-500 text-xs mt-1">
                  {errors.default_regime_logic}
                </div>
              )}
            </div>

            {formData.default_regime_logic === RegimeLogic.DOMESTIC_ONLY && (
              <div>
                <label
                  id="domestic_country_code-label"
                  className="form-label"
                  htmlFor="domestic_country_code"
                >
                  Domestic Country
                </label>
                {restrictedDomesticCountryCode ? (
                  <input
                    id="domestic_country_code"
                    type="text"
                    value="Finland"
                    className="input w-full"
                    disabled
                    readOnly
                  />
                ) : (
                  <CountryCodeSelect
                    id="domestic_country_code"
                    ariaLabel="Domestic Country"
                    className="w-full"
                    selected={formData.domestic_country_code || ''}
                    setFieldValue={(_dialCode: string, shortCode: string) => {
                      setFormData((prev) => ({
                        ...prev,
                        domestic_country_code: shortCode,
                      }));
                      setErrors((prev) => ({
                        ...prev,
                        domestic_country_code: '',
                      }));
                    }}
                    disabled={isReadOnly}
                    hasError={!!errors.domestic_country_code}
                    showDialCode={false}
                    useFullCountryName
                  />
                )}
                {errors.domestic_country_code && (
                  <div className="text-danger contrast:text-red-500 text-xs mt-1">
                    {errors.domestic_country_code}
                  </div>
                )}
              </div>
            )}

            {formData.default_regime_logic ===
              RegimeLogic.BY_CUSTOMER_COUNTRY && (
              <div>
                <label
                  id="customer_country_code-label"
                  className="form-label"
                  htmlFor="customer_country_code"
                >
                  Customer Country
                </label>
                <CountryCodeSelect
                  id="customer_country_code"
                  ariaLabel="Customer Country"
                  className="w-full"
                  selected={formData.customer_country_code || ''}
                  setFieldValue={(_dialCode: string, shortCode: string) => {
                    setFormData((prev) => ({
                      ...prev,
                      customer_country_code: shortCode,
                    }));
                    setErrors((prev) => ({
                      ...prev,
                      customer_country_code: '',
                    }));
                  }}
                  disabled={isReadOnly}
                  hasError={!!errors.customer_country_code}
                  showDialCode={false}
                  useFullCountryName
                />
                {errors.customer_country_code && (
                  <div className="text-danger contrast:text-red-500 text-xs mt-1">
                    {errors.customer_country_code}
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="form-label" htmlFor="active_from">
                Active From
              </label>
              <input
                id="active_from"
                type="date"
                name="active_from"
                value={formData.active_from}
                min={new Date().toISOString().split('T')[0]}
                onChange={handleChange}
                className="input w-full"
                disabled={isReadOnly}
                autoComplete="off"
              />
              {errors.active_from && (
                <div className="text-danger contrast:text-red-500 text-xs mt-1">
                  {errors.active_from}
                </div>
              )}
            </div>

            <div>
              <label className="form-label" htmlFor="active_to">
                Active To
              </label>
              <input
                id="active_to"
                type="date"
                name="active_to"
                value={formData.active_to}
                min={formData.active_from || ''}
                onChange={handleChange}
                className="input w-full"
                autoComplete="off"
                disabled={isReadOnly}
              />
              {errors.active_to && (
                <div className="text-danger contrast:text-red-500 text-xs mt-1">
                  {errors.active_to}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <button
              type="button"
              className="btn btn-light"
              onClick={() => navigate(-1)}
            >
              {isReadOnly ? 'Back' : 'Cancel'}
            </button>
            {!isReadOnly && (
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isCreating || isUpdating}
              >
                {isEdit ? 'Update' : 'Create'}
              </button>
            )}
          </div>
        </form>
      </div>
    </Container>
  );
};

export default ProductVatConfigForm;
